import {
  ActionResult,
  ApplicableContext,
  BaseAction,
  ExecuteContext,
} from '@cotera/client/v0/actions/framework';
import { z } from 'zod/v3';
import { COLUMN_ACTION_GROUP } from '../../constants';
import { GraphModel } from '@cotera/client/v0/graph/graph.model';
import { ExpressionColumnModel } from '../expression-column.model';
import { Assert, assertExists } from '@cotera/utilities';
import { DG } from '@cotera/sdk/core';
import { relColSchema } from '@cotera/api';
import {
  ModalPageView,
  toast,
  Loading,
  CollapsibleError,
} from '@cotera/client/app/components';
import { err, ok } from 'neverthrow';
import { AskResponse } from '@cotera/client/v0/actions/framework/types';
import { useState } from 'react';
import { Input } from '@cotera/client/v0/components/ui/input';
import { Label } from '@cotera/client/v0/components/ui/label';
import { Button } from '@cotera/client/v0/components/ui/button';
import { Params } from '@sentry/react/types/types';
import React from 'react';
import { findColumnNameConflict } from '../../utils/column-name-validation';

type SaveExprPayload = {};
type SaveExprError = { t: string; message?: string };

export class SaveExpressionColumnAction extends BaseAction<
  /* ActionPayload= */ SaveExprPayload,
  /* Errors= */ SaveExprError[]
> {
  override title = 'Save Column';
  override inputSchema = z.object({});
  shortcut = ['ctrl', 's'];
  group = COLUMN_ACTION_GROUP;
  icon = 'check';

  constructor(
    private readonly modalManager: {
      push: (props: {
        view: () => React.ReactNode;
        onClose?: () => void;
      }) => void;
      close: () => void;
    }
  ) {
    super();
  }

  /**
   * GraphModel & ExpressionColumnModel must be in scope. The exprModel's dataset must be ok.
   */
  override applicable(context: ApplicableContext): boolean {
    if (!context.isInScope(GraphModel)) {
      return false;
    }
    if (!context.hasTargetedModelOfType(ExpressionColumnModel)) {
      return false;
    }

    const exprColModel = assertExists(
      context.targetedModelOfType(ExpressionColumnModel)
    );

    const datasetRel = exprColModel.datasetRel.snapshot();
    if (datasetRel === null) {
      return false;
    }

    return datasetRel.isOk();
  }

  override async execute(
    _payload: SaveExprPayload,
    context: ExecuteContext
  ): Promise<ActionResult<SaveExprError[]>> {
    const exprModel = context.targetedModelOfType(ExpressionColumnModel);
    const graphModel = context.getInScopeModelOfType(GraphModel);

    // by `applicable()`
    Assert.assert(exprModel !== null);
    Assert.assert(graphModel !== null);

    // Check for column name conflicts in current dataset and parent lineage
    const columnName = exprModel.formState.snapshot().name;
    const conflictDataset = findColumnNameConflict(
      graphModel.graph.snapshot(),
      exprModel.datasetName,
      columnName,
      !exprModel.isNew // Exclude current column if it's a rename (not new)
    );

    if (conflictDataset !== null) {
      return err([
        {
          t: 'error',
          message: `A column with the name "${columnName}" already exists in dataset "${conflictDataset}"`,
        },
      ]);
    }

    const opSymbol = {
      name: exprModel.formState.snapshot().name,
      relName: exprModel.datasetName,
    };

    const opConfig: z.infer<typeof relColSchema> = {
      t: 'eraql',
      source: exprModel.formState.snapshot().eraQLEditorValue,
    };

    const operation = exprModel.isNew
      ? DG.Operations.createColumn({ ...opSymbol, config: opConfig })
      : DG.Operations.updateColumn({ ...opSymbol, config: opConfig });

    const tempCommit: DG.Commit = {
      actorId: 'system',
      timestamp: new Date(),
      operations: [operation],
      message: null,
    };

    // graphModel.compiler is already loaded with staged operations
    const datasetCompilationResult = graphModel.compiler
      .snapshot()
      .commit(tempCommit)
      .compile()
      .map(
        (x) =>
          x.getColumn({ name: opSymbol.name, rel: opSymbol.relName })?.config
      );

    Assert.assert(datasetCompilationResult !== undefined); // dataset exists at this point

    if (datasetCompilationResult.isErr()) {
      const errorMessage = datasetCompilationResult.error.prettyPrint();
      toast.error('Failed to compile column', {
        description: <CollapsibleError error={errorMessage} />,
      });
      return err([{ t: 'error', message: errorMessage }]);
    }
    const stagingResult = await graphModel.attemptToStageOperations([
      operation,
    ]);

    if (stagingResult.isErr()) {
      const errorMessage = stagingResult.error.prettyPrint();
      toast.error('Failed to stage operations', {
        description: <CollapsibleError error={errorMessage} />,
      });
      return err([{ t: 'error', message: errorMessage }]);
    }

    toast.success('Changes saved');

    return ok({});
  }

  shouldAsk(context: ApplicableContext): boolean {
    const exprColModel = context.targetedModelOfType(ExpressionColumnModel);
    Assert.assert(exprColModel !== null); // by `applicable`
    return exprColModel.isNew;
  }

  ask(_: {}, context: ExecuteContext): AskResponse<{}> {
    const exprColModel = context.targetedModelOfType(ExpressionColumnModel);
    Assert.assert(exprColModel !== null); // by `applicable`

    return {
      view: ({ onRespond, onCancel }) => {
        this.modalManager.push({
          view: () => {
            return (
              <AskView
                onRespond={async (params) => {
                  await onRespond(params);
                  this.modalManager.close();
                }}
                exprColModel={exprColModel}
              />
            );
          },
          onClose: async () => {
            await onCancel();
          },
        });
      },
    };
  }
}

export const AskView: React.FC<{
  onRespond: (params: Params) => Promise<void>;
  exprColModel: ExpressionColumnModel;
}> = ({ onRespond, exprColModel }) => {
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);

  const graph = exprColModel.inputs.graph.snapshot();
  const conflictDataset = findColumnNameConflict(
    graph,
    exprColModel.datasetName,
    name,
    false
  );

  const validName = conflictDataset === null && name.trim().length > 0;

  return (
    <ModalPageView
      title={`Save ${exprColModel.currentNameInGraph.snapshot()}`}
      className="px-4 py-4"
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={async (e) => {
          e.preventDefault();
        }}
      >
        <div className="flex flex-col gap-2">
          <Label htmlFor="expression-column-name" className="text-sm">
            Column Name
          </Label>
          {conflictDataset !== null && (
            <span className="text-xs text-destructive">
              A column with this name already exists in dataset "
              {conflictDataset}"
            </span>
          )}
          <Input
            id="expression-column-name"
            placeholder="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div className="w-full flex justify-end">
          <Button
            type="submit"
            variant="default"
            disabled={!validName || loading}
            icon="save"
            className="w-fit self-end"
            onClick={async () => {
              setLoading(true);
              React.startTransition(() => {
                exprColModel.formState.set({
                  ...exprColModel.formState.snapshot(),
                  name,
                });
                exprColModel.currentNameInGraph.set(name);
              });
              await onRespond({ to: name });
              setLoading(false);
            }}
          >
            {loading ? <Loading.Spinner variant="sm" /> : 'Save'}
          </Button>
        </div>
      </form>
    </ModalPageView>
  );
};
